import {
  AppEnum,
  getItem,
  type ItemNodeItemTypeEnum,
} from "@/services/internalApiV1";
import type { RouteLocationNormalized } from "vue-router";

import { getItemNodePublicRoute } from "./itemNodeRoutes";

/** The item listing has no public URL; fetch it once when following its link.
 * This runs before mounting the public page, so response forms never briefly
 * render in pupil mode. The resulting URL is the same one used by MaterialTile.
 *
 * Resolves to a path, or `undefined` when the route carries no gallery marker
 * for its own type. A lookup that fails (item discarded, membership gone, 403)
 * resolves to the listing the link came from: the row link carries the marker,
 * so middle-click and open-in-new-tab start here too, and returning `false`
 * there would abort the very first navigation and leave a blank page (review
 * of #13123, finding 5).
 */
export async function resolveItemNodeGalleryRoute(
  to: RouteLocationNormalized
): Promise<string | undefined> {
  const itemType = to.query.gallery_item_type;
  const id = (to.params as { id?: string }).id;
  if (
    typeof itemType !== "string" ||
    typeof id !== "string" ||
    getItemNodePublicRoute(itemType as ItemNodeItemTypeEnum) !== to.name
  ) {
    return undefined;
  }

  // Some old item nodes still carry Tools:: for the recorder/link namespaces.
  const objType = Object.values(AppEnum).find(
    (type) => type === itemType || `Tools::${type}` === itemType
  );
  if (!objType) return undefined;

  try {
    const item = await getItem(id, { objType });
    // Tools that are not catalogable (recorders, file sharing)
    // have no publicUrl; their public page is read-only without a token, so
    // they keep their existing destination. `itemNodeTypeToPublicRouteMapping`
    // lists only types for which that holds.
    // oxlint-disable-next-line typescript/prefer-nullish-coalescing -- an empty publicUrl must fall through to the route too
    const url = new URL(item.publicUrl || to.path, window.location.origin);
    for (const key of ["back_url", "back_title"]) {
      const value = to.query[key];
      if (typeof value === "string") url.searchParams.set(key, value);
    }
    url.searchParams.delete("gallery_item_type");
    // Route locally even when the configured API host differs (e.g. a worktree).
    return url.pathname + url.search + url.hash;
  } catch {
    // The API interceptor reports the error. Rather than mounting an
    // interactive response page without its token, go back to the listing the
    // link came from — a no-op for a click inside the app, a landing page for
    // a new tab.
    return listingPath(to.query.back_url);
  }
}

/**
 * The listing a gallery link came from, as a local path: `back_url` is the
 * row's own `window.location.href`, so it is same-origin by construction, but
 * it still arrives as query text — anything else falls back to the Gemeinsame
 * Ablage, the only listing that hands out these links to a non-owner.
 */
function listingPath(backUrl: unknown): string {
  const fallback = "/shared-folders";
  if (typeof backUrl !== "string") return fallback;

  try {
    const url = new URL(backUrl, window.location.origin);
    if (url.origin !== window.location.origin) return fallback;
    return url.pathname + url.search;
  } catch {
    return fallback;
  }
}
